import {
  Counters,
  CountersResult,
  CountersHelper,
  Article,
} from '@wix/answers-api';
import {
  UserToolbar,
  ToolbarContextSections,
  ContextData,
} from '@wix/answers-common-components';
import { useTranslation } from '@wix/wix-i18n-config';
import Long from 'long';
import { useRouter } from 'next/router';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { AGENT_TOOLBAR } from '../../constants';
import { useResponsive } from '../../hooks/useResponsive';
import { PageType } from '../../types';
import { answersApi } from '../../utils/answers';
import { useStore } from '../../zustand/stores';
import { PageData } from '../PageTemplate';

type AgentToolbarProps = {
  pageType: PageType;
  pageData: PageData;
};

const ToolbarContextSectionsMap = {
  [PageType.Article]: ToolbarContextSections.ARTICLE,
  [PageType.Category]: ToolbarContextSections.CATEGORY,
  [PageType.SearchResults]: ToolbarContextSections.NONE,
  [PageType.Homepage]: ToolbarContextSections.NONE,
  [PageType.RoadMap]: ToolbarContextSections.NONE,
  [PageType.Ticket]: ToolbarContextSections.NONE,
  [PageType.Profile]: ToolbarContextSections.NONE,
  [PageType.Contact]: ToolbarContextSections.NONE,
  [PageType.KnownIssues]: ToolbarContextSections.NONE,
};

const isProduction = process.env.NODE_ENV === 'production';

export const AgentToolbar: FunctionComponent<AgentToolbarProps> = ({
  pageType,
  pageData,
}) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const [isExpanded, setIsExpanded] = useState(true);
  const [contextData, setContextData] = React.useState<ContextData>({});
  const [counters, setCounters] = React.useState<Counters>();
  const [relatedTicketsCount, setRelatedTicketsCount] = React.useState(0);
  const { isMobile } = useResponsive();
  const { isWixEmployee: isAgent } = useStore((state) => state.userSlice);
  const showAgentToolbar = useMemo(
    () => isAgent && !isMobile,
    [isAgent, isMobile]
  );

  const fetchRelatedTicketsCount = useCallback(async () => {
    const { article } = pageData;
    if (pageType === PageType.Article && article?.id && showAgentToolbar && !article.isFromV2) {
      if (isProduction) {
        const relatedTickets = await answersApi.tickets.searchAdmin({
          filters: { relatedArticleIds: [article.id] },
          locale: article.locale,
        });
        setRelatedTicketsCount(relatedTickets.itemsCount);
      }
    }
  }, [pageData, pageType, answersApi, showAgentToolbar, isProduction]);

  const fetchCounters = useCallback(async () => {
    const { article } = pageData;
    if (
      isProduction &&
      pageType === PageType.Article &&
      article?.id &&
      showAgentToolbar &&
      !article.isFromV2
    ) {
      let newCounters: Counters;

      try {
        const articleStatistics = await answersApi.kb.getArticleStatistics({
          id: article?.id,
          locale: locale as string,
        });
        newCounters = {
          helpful: Number(articleStatistics.total_helpful_votes),
          unhelpful: Number(articleStatistics.total_unhelpful_votes),
          views: Number(articleStatistics.last_90_days_views),
          votes: Number(articleStatistics.total_general_votes),
          followers: Number(articleStatistics.total_general_votes),
        };
      } catch (err) {
        const res = await answersApi.articles.getArticleCounters({
          id: article?.id,
          locale: locale as string,
        });
        newCounters = CountersHelper.convertCounterResultsToCounters(
          res as unknown as CountersResult[]
        );
      }

      setCounters(newCounters);
    }
  }, [locale, showAgentToolbar, pageData, pageType, answersApi]);

  useEffect(() => {
    void fetchCounters();
  }, [fetchCounters]);

  useEffect(() => {
    void fetchRelatedTicketsCount();
  }, [fetchRelatedTicketsCount]);

  const formatDate = (
    date?: number | { low: number; high: number; unsigned?: boolean }
  ): string => {
    if (!date) {
      return '';
    }
    const epochMs =
      typeof date === 'number'
        ? date
        : new Long(date.low, date.high, date.unsigned).toNumber();
    return epochMs
      ? new Date(epochMs).toLocaleDateString('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '';
  };

  useEffect(() => {
    const { article, category } = pageData;
    switch (pageType) {
      case PageType.Article:
        if (article) {
          setContextData({
            articleContext: {
              article: {
                ...(article as unknown as Article),
                relatedTicketsCount:
                  relatedTicketsCount || article.relatedTicketsCount || 0,
              },
              updated: formatDate(article.lastPublishDate),
              created: formatDate(article.creationDate),
              ...(article.isFromV2
                ? {
                    manageArticleUrl: `https://wix-bo.com/kb-backoffice-fed/articles/${article.id}/edit`,
                  }
                : { counters }),
            },
          } as unknown as ContextData);
        }
        break;
      case PageType.Category:
        if (category) {
          setContextData({
            categoryContext: {},
          });
        }
        break;
    }
  }, [pageData, pageType, counters, relatedTicketsCount]);

  useEffect(() => {
    const bodyElm = document.body;
    if (showAgentToolbar) {
      if (isExpanded) {
        bodyElm.classList.add(AGENT_TOOLBAR.CLASSES.OPEN);
        bodyElm.classList.remove(AGENT_TOOLBAR.CLASSES.CLOSE);
      } else {
        bodyElm.classList.add(AGENT_TOOLBAR.CLASSES.CLOSE);
        bodyElm.classList.remove(AGENT_TOOLBAR.CLASSES.OPEN);
      }
    }
  }, [isExpanded, showAgentToolbar]);

  const getVisibleContext = () =>
    ToolbarContextSectionsMap[pageType] || ToolbarContextSections.NONE;

  return showAgentToolbar ? (
    <UserToolbar
      isExpanded={isExpanded}
      toggleExpansion={() => setIsExpanded(!isExpanded)}
      translate={t}
      visibleContext={getVisibleContext()}
      contextData={contextData}
    />
  ) : null;
};
